// SPDX-License-Identifier: AGPL-3.0-or-later

import {marketingUrl} from '@app/features/messaging/utils/MessagingUrlUtils';

export const Routes = {
	HOME: '/',
	APP: '/app',
	LOGIN: '/login',
	REGISTER: '/register',
	FORGOT_PASSWORD: '/forgot',
	RESET_PASSWORD: '/reset',
	RECOVER_ACCOUNT: '/recover',
	VERIFY_EMAIL: '/verify',
	AUTHORIZE_IP: '/authorize-ip',
	EMAIL_REVERT: '/wasntme',
	PENDING: '/pending',
	OAUTH_AUTHORIZE: '/oauth2/authorize',
	SSO_CALLBACK: '/auth/sso/callback',
	INVITE_REGISTER: '/invite/:code',
	INVITE_LOGIN: '/invite/:code/login',
	GIFT_REGISTER: '/gift/:code',
	GIFT_LOGIN: '/gift/:code/login',
	THEME_REGISTER: '/theme/:themeId',
	THEME_LOGIN: '/theme/:themeId/login',
	ME: '/channels/@me',
	FAVORITES: '/channels/@favorites',
	DISCOVER: '/channels/@discover',
	BOOKMARKS: '/bookmarks',
	MENTIONS: '/mentions',
	NOTIFICATIONS: '/notifications',
	YOU: '/you',
	REPORT: '/report',
	PREMIUM_CALLBACK: '/premium-callback',
	AGE_VERIFICATION_CALLBACK: '/age-verification-callback',
	CONNECTION_CALLBACK: '/connection-callback/:type',
	USER_PROFILE: '/users/:userId',
	THEME_STUDIO: '/theme-studio',
	// Micup: the microphone test opens without signing in, from the marketing site.
	MIC_TEST: '/mic-test',
	FEED: '/feed',
	FEED_POST: '/feed/posts/:postId',
	// Micup: premium and the shop are pages beside the DM list, like bookmarks and mentions.
	PREMIUM: '/premium',
	SHOP: '/shop',
	BOTS: '/bots',
	LEADERBOARDS: '/leaderboards',
	// Micup: the same pages opened from the feed stay beside its sidebar (FeedSectionPages).
	FEED_PREMIUM: '/feed/premium',
	FEED_SHOP: '/feed/shop',
	FEED_BOTS: '/feed/bots',
	FEED_LEADERBOARDS: '/feed/leaderboards',
	PLUTONIUM: '/channels/@premium',
	LEGACY_PLUTONIUM: '/plutonium',
	terms: () => marketingUrl('terms'),
	privacy: () => marketingUrl('privacy'),
	guidelines: () => marketingUrl('guidelines'),
	careers: () => marketingUrl('careers'),
	partners: () => marketingUrl('partners'),
	bugs: () => marketingUrl('help/report-bug'),
	plutonium: () => marketingUrl('plutonium'),
	help: () => marketingUrl('help'),
	helpArticle: (slug: string) => marketingUrl(`help/${slug}`),
	dmChannel: (channelId: string) => `/channels/@me/${channelId}`,
	favoritesChannel: (channelId: string) => `/channels/@favorites/${channelId}`,
	guildMembers: (guildId: string) => `/channels/${guildId}/members`,
	guildChannel: (guildId: string, channelId?: string) =>
		channelId ? `/channels/${guildId}/${channelId}` : `/channels/${guildId}`,
	channelMessage: (guildId: string, channelId: string, messageId: string) =>
		`${Routes.guildChannel(guildId, channelId)}/${messageId}`,
	threadPanel: (guildId: string, channelId: string, threadId: string, messageId?: string) =>
		`${Routes.guildChannel(guildId, channelId)}/threads/${threadId}${messageId ? `/${messageId}` : ''}`,
	dmChannelMessage: (channelId: string, messageId: string) => `${Routes.dmChannel(channelId)}/${messageId}`,
	favoritesChannelMessage: (channelId: string, messageId: string) =>
		`${Routes.favoritesChannel(channelId)}/${messageId}`,
	inviteRegister: (code: string) => `/invite/${code}`,
	inviteLogin: (code: string) => `/invite/${code}/login`,
	giftRegister: (code: string) => `/gift/${code}`,
	giftLogin: (code: string) => `/gift/${code}/login`,
	theme: (themeId: string) => `/theme/${themeId}`,
	themeRegister: (themeId: string) => `/theme/${themeId}`,
	themeLogin: (themeId: string) => `/theme/${themeId}/login`,
	userProfile: (userId: string) => `/users/${userId}`,
	feedPost: (postId: string) => `/feed/posts/${postId}`,
	isSpecialPage: (pathname: string) =>
		pathname === Routes.BOOKMARKS ||
		pathname === Routes.MENTIONS ||
		pathname === Routes.NOTIFICATIONS ||
		pathname === Routes.YOU ||
		pathname === Routes.PREMIUM ||
		pathname === Routes.SHOP ||
		pathname === Routes.BOTS ||
		pathname === Routes.LEADERBOARDS,
	isDMRoute: (pathname: string) => pathname.startsWith('/channels/@me'),
	isFeedRoute: (pathname: string) => pathname === Routes.FEED || pathname.startsWith(`${Routes.FEED}/`),
	isFavoritesRoute: (pathname: string) => pathname.startsWith('/channels/@favorites'),
	isDiscoverRoute: (pathname: string) => pathname.startsWith('/channels/@discover'),
	isPlutoniumRoute: (pathname: string) => pathname === '/channels/@premium',
	isChannelRoute: (pathname: string) => pathname.startsWith('/channels/') && !pathname.startsWith('/channels/@premium'),
	isGuildChannelRoute: (pathname: string) =>
		pathname.startsWith('/channels/') &&
		!pathname.startsWith('/channels/@me') &&
		!pathname.startsWith('/channels/@favorites') &&
		!pathname.startsWith('/channels/@discover') &&
		!pathname.startsWith('/channels/@premium'),
} as const;
